/**
 * Expanded detail for a `commercial_digest` feed root. Renders the search
 * cycle's actionable opportunities for one commercial dimension (rebook /
 * first-book) for a single portfolio owner.
 *
 * Each booking row *is* the review / booking-detail CTA (carrier → contract,
 * lane + dates honoring user prefs, and agent / shipper / consignee). A
 * A persistent "Show all" link opens the bookings list with digest filters.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, ArrowUpRight } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import {
  SensitiveContractRef,
  SensitiveInlineText,
  SensitiveMoney,
  SensitiveText,
} from "@/components/common/Sensitive";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes, formatLaneKey } from "@/lib/formatPort";
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import { formatPersonName } from "@/lib/formatPersonName";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import { formatUsd } from "@/types/booking";
import type { ApiBooking, RecommendationItem } from "@/types/api";
import type {
  CommercialDigestBooking,
  CommercialDigestPayload,
} from "@/types/feed";
import {
  collectOpportunityRestrictionNotes,
  partitionOpportunityRestrictionNotes,
} from "@/features/bookings/opportunityInsights";
import { isOpenOpportunity } from "@/features/bookings/bookingOpportunities";
import { formatDigestFirstBookAmount } from "@/features/bookings/lclFirstBookOpportunity";
import { bookingHref } from "./feedLinks";

/** Whether a payload is a `commercial_digest` root. */
export function isCommercialDigestPayload(
  payload: unknown,
): payload is CommercialDigestPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "commercial_digest" && Array.isArray(p.bookings);
}

function firstText(
  ...values: Array<string | null | undefined>
): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function bookingLaneKey(booking: ApiBooking | null): string | undefined {
  const origin = booking?.origin_port?.locode?.trim();
  const dest = booking?.destination_port?.locode?.trim();
  if (!origin || !dest) return undefined;
  return `${origin}-${dest}`;
}

export interface DigestRowExtras {
  nextSailingEtd?: string;
  nextSailingVessel?: string;
  warnings: string[];
  otherAlternatives: number;
  /** First-book quote label (LCL W/M when cargo dims are unknown). */
  firstBookAmountLabel?: string;
}

const EMPTY_EXTRAS: DigestRowExtras = {
  warnings: [],
  otherAlternatives: 0,
};

/** Suggested alternative sailing on the recommendation (ETD + optional vessel). */
export function digestNextSailing(rec: RecommendationItem | null): {
  etd?: string;
  vessel?: string;
} {
  if (!rec) return {};
  const etd = firstText(rec.suggested_etd, rec.schedule_match?.etd);
  const vessel = firstText(rec.schedule_match?.vessel_name);
  return { etd, vessel };
}

export function digestWarningLines(rec: RecommendationItem | null): string[] {
  if (!rec) return [];
  const { warnings } = partitionOpportunityRestrictionNotes(
    collectOpportunityRestrictionNotes(rec),
  );
  return warnings.map((note) =>
    note.label ? `${note.label} — ${note.reason}` : note.reason,
  );
}

/** Other open alternatives for the same booking, excluding the shown rec. */
export function countOtherAlternatives(
  recs: RecommendationItem[],
  pickedId?: string | null,
): number {
  return recs.filter((rec) => {
    if (pickedId && rec.id === pickedId) return false;
    if (!isOpenOpportunity(rec)) return false;
    if (rec.is_blocked) return false;
    return rec.savings > 0 || rec.is_first_book === true;
  }).length;
}

export function buildDigestRowExtras(
  rec: RecommendationItem | null,
  recs: RecommendationItem[],
  row?: CommercialDigestBooking,
): DigestRowExtras {
  const sailing = digestNextSailing(rec);
  return {
    nextSailingEtd: sailing.etd,
    nextSailingVessel: sailing.vessel,
    warnings: digestWarningLines(rec),
    otherAlternatives: countOtherAlternatives(recs, rec?.id),
    firstBookAmountLabel:
      rec?.is_first_book && row
        ? formatDigestFirstBookAmount(row, rec)
        : undefined,
  };
}

export function pickDigestRecommendation(
  recs: RecommendationItem[],
  row: CommercialDigestBooking,
): RecommendationItem | null {
  if (row.recommendation_id) {
    const match = recs.find((r) => r.id === row.recommendation_id);
    if (match) return match;
  }
  return (
    recs.find((r) => !r.is_blocked && (r.savings > 0 || r.is_first_book)) ??
    recs[0] ??
    null
  );
}

/** Merge live booking / recommendation fields onto a digest row. */
export function mergeDigestBooking(
  row: CommercialDigestBooking,
  booking: ApiBooking | null,
  rec: RecommendationItem | null,
): CommercialDigestBooking {
  return {
    ...row,
    lane_key: firstText(row.lane_key, bookingLaneKey(booking)),
    original_carrier: firstText(
      row.original_carrier,
      rec?.original_carrier,
      booking?.carrier,
    ),
    original_contract_ref: firstText(
      row.original_contract_ref,
      rec?.original_contract_ref,
      booking?.contract_ref,
    ),
    alternative_carrier: firstText(
      row.alternative_carrier,
      rec?.alternative_carrier,
    ),
    alternative_contract_ref: firstText(
      row.alternative_contract_ref,
      rec?.alternative_contract_ref,
    ),
    etd: firstText(
      row.etd,
      rec?.booking_etd,
      booking?.operational?.etd,
      booking?.departure_date,
    ),
    cutoff: firstText(
      row.cutoff,
      rec?.booking_cutoff_date,
      booking?.effective_cutoff,
      booking?.cutoff_date,
    ),
    agent: firstText(row.agent, rec?.booking_agent, booking?.agent),
    shipper: firstText(
      row.shipper,
      rec?.booking_shipper,
      booking?.shipper_name,
      booking?.shipper,
    ),
    consignee: firstText(
      row.consignee,
      rec?.booking_consignee,
      booking?.consignee,
    ),
    equipment_summary: firstText(
      row.equipment_summary,
      rec?.booking_equipment_summary,
      booking?.equipment_summary,
    ),
  };
}

function digestNeedsHydrate(row: CommercialDigestBooking): boolean {
  return (
    !row.shipper ||
    !row.consignee ||
    !row.agent ||
    !row.etd ||
    !row.original_carrier ||
    !row.original_contract_ref ||
    !row.alternative_contract_ref ||
    !row.equipment_summary
  );
}

export type DigestDisplayRow = CommercialDigestBooking & DigestRowExtras;

/** Stable identity for hydration — callers often pass a fresh array each render. */
export function commercialDigestHydrateKey(
  bookings: CommercialDigestBooking[],
): string {
  return bookings
    .map((row) =>
      [
        row.source_id,
        row.recommendation_id ?? "",
        row.shipper ?? "",
        row.consignee ?? "",
        row.agent ?? "",
        row.etd ?? "",
        row.cutoff ?? "",
        row.original_carrier ?? "",
        row.original_contract_ref ?? "",
        row.alternative_carrier ?? "",
        row.alternative_contract_ref ?? "",
        row.equipment_summary ?? "",
        row.lane_key ?? "",
        row.savings_usd ?? "",
        row.first_book_price ?? "",
      ].join(":"),
    )
    .join("|");
}

export function useHydratedDigestBookings(bookings: CommercialDigestBooking[]) {
  const [rows, setRows] = useState<DigestDisplayRow[]>(() =>
    bookings.map((row) => ({ ...row, ...EMPTY_EXTRAS })),
  );

  const hydrateKey = commercialDigestHydrateKey(bookings);

  useEffect(() => {
    let cancelled = false;
    setRows(bookings.map((row) => ({ ...row, ...EMPTY_EXTRAS })));
    void Promise.all(
      bookings.map(async (row) => {
        const [booking, recs] = await Promise.all([
          digestNeedsHydrate(row)
            ? api.getBooking(row.source_id).catch(() => null)
            : Promise.resolve(null),
          api.getBookingRecommendations(row.source_id).catch(() => []),
        ]);
        const rec = pickDigestRecommendation(recs, row);
        const merged = mergeDigestBooking(row, booking, rec);
        return {
          ...merged,
          ...buildDigestRowExtras(rec, recs, merged),
        };
      }),
    ).then((merged) => {
      if (!cancelled) setRows(merged);
    });

    return () => {
      cancelled = true;
    };
    // payload.bookings / inline `[row]` are new arrays each render; key on row content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateKey]);

  return rows;
}

function ContractSide({
  label,
  carrier,
  contractRef,
  nextSailing,
}: {
  label: string;
  carrier?: string;
  contractRef?: string;
  nextSailing?: string | null;
}) {
  return (
    <div className="feed-digest-side">
      <p className="feed-digest-side__label">{label}</p>
      <div className="feed-digest-side__body">
        {carrier ? (
          <CarrierLogo carrier={carrier} size={16} bold />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
        {contractRef ? (
          <span className="feed-digest-side__contract">
            <SensitiveContractRef value={contractRef} carrier={carrier} />
          </span>
        ) : null}
      </div>
      {nextSailing && (
        <p className="feed-digest-side__sailing">{nextSailing}</p>
      )}
    </div>
  );
}

function PartyRow({
  label,
  value,
  formatName = true,
}: {
  label: string;
  value?: string;
  /** When false, show the raw end-client code (consignee). */
  formatName?: boolean;
}) {
  if (!value) return null;
  const display = formatName
    ? formatPersonName(value)
    : formatDimensionValue("consignee", value);
  return (
    <div className="feed-digest-party">
      <dt>{label}</dt>
      <dd>
        <SensitiveText value={display} />
      </dd>
    </div>
  );
}

export function DigestBookingRow({
  booking,
  isRebook,
  showLocodes,
  portNames,
  formatDate,
}: {
  booking: DigestDisplayRow;
  isRebook: boolean;
  showLocodes: boolean;
  portNames: ReadonlyMap<string, string>;
  formatDate: (iso: string | null | undefined) => string;
}) {
  const savingsPct =
    isRebook && booking.savings_pct != null
      ? `${Math.round(booking.savings_pct * 100)}%`
      : null;
  const lane = booking.lane_key
    ? formatLaneKey(booking.lane_key, showLocodes, portNames)
    : null;
  const hasContractFlow =
    booking.original_carrier ||
    booking.original_contract_ref ||
    booking.alternative_carrier ||
    booking.alternative_contract_ref;
  const hasParties = booking.agent || booking.shipper || booking.consignee;
  const amount = isRebook
    ? formatUsd(booking.savings_usd)
    : (booking.firstBookAmountLabel ??
      formatDigestFirstBookAmount(booking));
  const nextSailing = [
    booking.nextSailingEtd ? formatDate(booking.nextSailingEtd) : null,
    booking.nextSailingVessel,
  ]
    .filter(Boolean)
    .join(" · ");
  const nextSailingLabel = nextSailing ? `Next sailing ${nextSailing}` : null;
  const otherHint =
    booking.otherAlternatives > 0
      ? `${booking.otherAlternatives} other alternative${
          booking.otherAlternatives === 1 ? "" : "s"
        }`
      : null;

  return (
    <Link
      to={bookingHref(booking.source_id)}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "feed-digest-row",
        isRebook ? "feed-digest-row--rebook" : "feed-digest-row--first-book",
      )}
      aria-label={`${isRebook ? "Rebook" : "First-book"} booking ${booking.source_id}`}
    >
      <div className="feed-digest-row__top">
        <div className="feed-digest-row__identity">
          <span className="feed-digest-row__id">
            #<SensitiveInlineText text={booking.source_id} />
            <ArrowUpRight className="size-3" />
          </span>
          {booking.equipment_summary && (
            <span className="feed-digest-row__equip">
              {booking.equipment_summary}
            </span>
          )}
        </div>
        <div className="feed-digest-row__saving">
          <span className="feed-digest-row__saving-line">
            <span className="feed-digest-row__saving-kind">
              {isRebook ? "Save" : "Quote"}
            </span>
            <span
              className={cn(
                "feed-digest-row__saving-amount",
                !isRebook && "feed-digest-row__saving-amount--quote",
              )}
            >
              <SensitiveMoney formatted={amount} />
            </span>
          </span>
          {savingsPct && (
            <span className="feed-digest-row__saving-pct">
              <SensitiveMoney formatted={savingsPct} />
            </span>
          )}
          {booking.match_type && (
            <span className="feed-digest-row__saving-meta">
              {booking.match_type}
            </span>
          )}
          {otherHint && (
            <span className="feed-digest-row__hint">{otherHint}</span>
          )}
        </div>
      </div>

      {(lane || booking.etd || booking.cutoff) && (
        <div className="feed-digest-row__meta">
          {lane && <span>{lane}</span>}
          {booking.etd && <span>ETD {formatDate(booking.etd)}</span>}
          {booking.cutoff && <span>Cutoff {formatDate(booking.cutoff)}</span>}
        </div>
      )}

      {hasContractFlow && (
        <div
          className={cn(
            "feed-digest-flow",
            !isRebook && "feed-digest-flow--quoted",
          )}
        >
          {isRebook ? (
            <>
              <ContractSide
                label="From"
                carrier={booking.original_carrier}
                contractRef={booking.original_contract_ref}
              />
              <ArrowRight className="feed-digest-flow__arrow" aria-hidden />
              <ContractSide
                label="To"
                carrier={booking.alternative_carrier}
                contractRef={booking.alternative_contract_ref}
                nextSailing={nextSailingLabel}
              />
            </>
          ) : (
            <ContractSide
              label="Quoted"
              carrier={booking.alternative_carrier}
              contractRef={booking.alternative_contract_ref}
              nextSailing={nextSailingLabel}
            />
          )}
        </div>
      )}

      {hasParties && (
        <dl className="feed-digest-parties">
          <PartyRow label="Handling" value={booking.agent} />
          <PartyRow label="Shipper" value={booking.shipper} />
          <PartyRow label="Consignee" value={booking.consignee} formatName={false} />
        </dl>
      )}

      {booking.warnings.length > 0 && (
        <div className="feed-digest-warnings">
          <p className="feed-digest-warnings__label">
            <AlertTriangle className="size-2.5" aria-hidden />
            Warnings
          </p>
          <ul>
            {booking.warnings.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </Link>
  );
}

export function FeedCommercialDigestDetail({
  payload,
}: {
  payload: CommercialDigestPayload;
}) {
  const { preferences } = useUserPreferences();
  const { formatDate } = useDateFormatter();
  const bookings = useHydratedDigestBookings(payload.bookings);
  const laneLocodes = extractLaneLocodes(...bookings.map((b) => b.lane_key));
  const portNames = usePortNames(laneLocodes);

  const isRebook = payload.dimension === "rebook_opportunity";
  const total = payload.booking_count;

  return (
    <div
      className={cn(
        "feed-digest",
        isRebook ? "feed-digest--rebook" : "feed-digest--first-book",
      )}
    >
      <p className="feed-digest__summary feed-row-detail-body__heading">
        {total} {isRebook ? "rebook" : "first booking"} opportunit
        {total === 1 ? "y" : "ies"}
        {isRebook && payload.total_savings_usd != null ? (
          <>
            {" · "}
            <SensitiveMoney amount={payload.total_savings_usd} />
            {" potential savings"}
          </>
        ) : null}
      </p>

      <div className="feed-digest__list">
        {bookings.map((booking) => (
          <DigestBookingRow
            key={booking.source_id}
            booking={booking}
            isRebook={isRebook}
            showLocodes={preferences.showLocodes}
            portNames={portNames}
            formatDate={formatDate}
          />
        ))}
      </div>
    </div>
  );
}
